import { useEffect, useRef, type RefObject } from 'react';

const OUTSIDE_PRESS_EVENT = 'mousedown';

export type OutsidePressRefs = ReadonlyArray<RefObject<HTMLElement | null>>;

/**
 * Checks whether a node belongs to any of the given elements.
 */
export function isInsideAnyRef({
  node,
  refs,
}: {
  node: Node;
  refs: OutsidePressRefs;
}): boolean {
  return refs.some((ref: RefObject<HTMLElement | null>): boolean =>
    Boolean(ref.current?.contains(node))
  );
}

/**
 * Calls `onOutsidePress` for every press that lands outside `insideRefs`.
 * Listens in the capture phase so a control that stops propagation still
 * dismisses, and never cancels the press, so it keeps reaching its target.
 */
export function useOutsidePress({
  insideRefs,
  onOutsidePress,
}: {
  insideRefs: OutsidePressRefs;
  onOutsidePress: (pressTarget: Node) => void;
}): void {
  const insideRefsRef = useRef<OutsidePressRefs>(insideRefs);
  const onOutsidePressRef = useRef<(pressTarget: Node) => void>(onOutsidePress);

  insideRefsRef.current = insideRefs;
  onOutsidePressRef.current = onOutsidePress;

  useEffect(() => {
    const handleDocumentPress = (event: Event): void => {
      const pressTarget: Node | null = event.target as Node | null;

      if (!pressTarget) {
        return;
      }

      if (isInsideAnyRef({ node: pressTarget, refs: insideRefsRef.current })) {
        return;
      }

      onOutsidePressRef.current(pressTarget);
    };

    document.addEventListener(OUTSIDE_PRESS_EVENT, handleDocumentPress, true);

    return () => {
      document.removeEventListener(
        OUTSIDE_PRESS_EVENT,
        handleDocumentPress,
        true
      );
    };
  }, []);
}
